import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SnackBarService } from '@fuse/services/snack-bar';
import { UserService } from 'app/core/user/user.service';
import { map, take } from 'rxjs';

export const permissionGuard: CanActivateFn = (route, state) => {
  const userService = inject(UserService);
  const snackBarService = inject(SnackBarService);
  const router = inject(Router);

  return userService.setting$.pipe(
    take(1),
    map(res => {
      const permissions: any[] =
        res?.data?.permissions || res?.data?.user_details?.permissions || [];
      let accessKey: any = route?.data?.accessKey;
      if (!accessKey) return false;
      if (!Array.isArray(accessKey)) {
        accessKey = [accessKey];
      };
      const normalizedPermissions = permissions.map(p => p.toLowerCase());
      const requiredPermissions = accessKey.map(key =>
        key.startsWith("!") ? key.substring(1).toLowerCase(): key.toLowerCase());
      const hasAnyPermission = requiredPermissions.some(req =>normalizedPermissions.includes(req));

      if (!hasAnyPermission) {
        snackBarService.error('Access Denied');
        router.navigate(['not-found'], {
          state: { title: 'Access Denied', message: 'You do not have permission to access this page.' }
        });
        return false;
      };

      // Build additionalAccess
      // Rule: If accesskey starts with "!", do not add all prefix actions—only add its own action, and only if that exact permission exists.
      let additionalAccess: string[] = [];
      accessKey.forEach(key => {
        const isNegated = key.startsWith("!");
        const cleanKey = isNegated ? key.substring(1) : key;
        const prefix = cleanKey.substring(0, cleanKey.lastIndexOf("."));
        const action = cleanKey.split(".").pop()!;
        if (isNegated) {
          if (normalizedPermissions.includes(cleanKey.toLowerCase())) {
            additionalAccess.push(action);
          }
          return;
        };
        const userHasPrefix = permissions.some(p => p.startsWith(prefix + "."));
        if (!userHasPrefix) {
          return;
        };
        const matches = permissions.filter(p => p.startsWith(prefix + "."))
          .map(p => p.split(".").pop()!)
          .filter(Boolean);
        additionalAccess.push(...matches);
      });
      additionalAccess = [...new Set(additionalAccess)];
      route.data = { ...route.data, additionalAccess };
      return true;
    })
  );
};
